import { Form, Formik } from 'formik'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { getDateFromYearAndMonth } from '../../../lib/Dates'
import { SpendCreate } from '../../../lib/Types'
import { isCurrency } from '../../../lib/Validations'
import Headline from '../../Elements/Headline'
import { Modal } from '../../Elements/Modal'
import Button from '../../Formik/Button'
import Input from '../../Formik/Input'
import Select from '../../Formik/Select'
import Textarea from '../../Formik/Textarea'
import { DistributionType } from '../lib/Enums/DistributionType'
import { SpendFrequency } from '../lib/Enums/SpendFrequency'
import { SpendType } from '../lib/Enums/SpendType'
import { useBudget } from '../lib/Hooks/Budget'
import { useAddSpend } from '../lib/Hooks/Spends'

interface Props {
  toggleHide: () => void
  show: boolean
  saved?: () => void
}

const AddSaving: FC<Props> = ({ toggleHide, show, saved }) => {
  const { t } = useTranslation(['budget', 'common'])
  const { data: budget } = useBudget()
  const { mutate: addSpend } = useAddSpend(budget!)

  const save = async (spend: SpendCreate) => {
    addSpend(spend)

    toggleHide()

    if (saved) saved()
  }

  return (
    <Modal show={show} onHide={toggleHide}>
      {(closeModal: any) => (
        <>
          <Headline className='mb-4'>{t('budget:addSaving.title')}</Headline>
          <Formik
            initialValues={{
              title: '',
              description: '',
              frequency: SpendFrequency.MONTHLY,
              amount: 0,
              payment_month: '1',
            }}
            validationSchema={Yup.object({
              title: Yup.string().required(t('common:validation.required')),
              frequency: Yup.string().required(
                t('common:validation.required'),
              ),
              amount: Yup.string()
                .transform((currentValue) => {
                  // First remove all danish thousand seperators (.) and then replace the danish delimiter seperator (,) with the english one (.).
                  // This way it's possible to save floats.
                  return currentValue.replaceAll('.', '').replaceAll(',', '.')
                })
                .required(t('common:validation.required'))
                .test(isCurrency),
              payment_month: Yup.string().required(
                t('common:validation.required'),
              ),
            })}
            onSubmit={(values) => {
              const { frequency, amount, ...rest } = values

              const newSpend = {
                ...rest,
                type: SpendType.SAVING,
                accounting_date: getDateFromYearAndMonth(
                  new Date().getFullYear(),
                  parseInt(values.payment_month, 10),
                ),
                distribution_type: DistributionType.CURRENCY,
                distribution_primary_user: amount,
                amounts: [
                  {
                    formula: amount.toString(),
                    frequency,
                  },
                ],
              }
              save(newSpend)
              closeModal()
            }}
          >
            <AddSpendForm />
          </Formik>
        </>
      )}
    </Modal>
  )
}

const AddSpendForm = () => {
  const { t } = useTranslation(['budget'])

  return (
    <Form>
      <>
        <p className='mb-2'>{t('budget:addSaving.name')}</p>
        <Input
          name='title'
          placeholder={t('budget:addSaving.namePlaceholder')}
          className='mb-4'
        />
        <p className='mb-2'>{t('budget:shared.note')}</p>
        <Textarea
          name='description'
          placeholder={t('budget:addSaving.notePlaceholder')}
          className='mb-4'
        />
      </>
      <p className='mb-2'>{t('budget:addSaving.frequencyLabel')}</p>
      <Select name='frequency' className='mb-4'>
        <option value={SpendFrequency.MONTHLY}>
          {t('budget:shared.frequency.monthly')}
        </option>
        <option value={SpendFrequency.BIMONTHLY}>
          {t('budget:shared.frequency.bimonthly')}
        </option>
        <option value={SpendFrequency.QUARTERLY}>
          {t('budget:shared.frequency.quarterly')}
        </option>
        <option value={SpendFrequency.BIYEARLY}>
          {t('budget:shared.frequency.biyearly')}
        </option>
        <option value={SpendFrequency.YEARLY}>
          {t('budget:shared.frequency.yearly')}
        </option>
      </Select>
      <p className='mb-2'>{t('budget:addSaving.amountLabel')}</p>
      <Input
        type='number'
        name='amount'
        placeholder={t('budget:shared.amountPlaceholder')}
        className='mb-4'
      />
      <Button type='submit'>{t('budget:shared.add')}</Button>
    </Form>
  )
}

export default AddSaving
